// Curated tag-color palette. The server stores a color token (e.g. "sky");
// we map it to a class pair (background + text) at render time so we get
// proper light/dark mode behaviour from Tailwind.
//
// Future AI-tagged threads will pick from the same palette so the badges
// look uniform regardless of source.

export const TAG_COLORS = [
  'slate',
  'gray',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
] as const;

export type TagColor = (typeof TAG_COLORS)[number];

// Static class map. Tailwind's JIT needs the literal strings present in
// source so it doesn't tree-shake them.
const CLASSES: Record<string, string> = {
  slate: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 ring-slate-500/20',
  gray: 'bg-gray-500/15 text-gray-700 dark:text-gray-300 ring-gray-500/20',
  red: 'bg-red-500/15 text-red-700 dark:text-red-300 ring-red-500/20',
  orange: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 ring-orange-500/20',
  amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/20',
  yellow: 'bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 ring-yellow-500/20',
  lime: 'bg-lime-500/15 text-lime-700 dark:text-lime-300 ring-lime-500/20',
  green: 'bg-green-500/15 text-green-700 dark:text-green-300 ring-green-500/20',
  emerald: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/20',
  teal: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 ring-teal-500/20',
  cyan: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 ring-cyan-500/20',
  sky: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-500/20',
  blue: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-blue-500/20',
  indigo: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 ring-indigo-500/20',
  violet: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-violet-500/20',
  purple: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 ring-purple-500/20',
  fuchsia: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 ring-fuchsia-500/20',
  pink: 'bg-pink-500/15 text-pink-700 dark:text-pink-300 ring-pink-500/20',
  rose: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/20',
};

export function tagClasses(color: string): string {
  return CLASSES[color] ?? CLASSES.slate!;
}

// For the color-picker swatches.
const DOT_CLASSES: Record<string, string> = {
  slate: 'bg-slate-500',
  gray: 'bg-gray-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  yellow: 'bg-yellow-500',
  lime: 'bg-lime-500',
  green: 'bg-green-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  sky: 'bg-sky-500',
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
  purple: 'bg-purple-500',
  fuchsia: 'bg-fuchsia-500',
  pink: 'bg-pink-500',
  rose: 'bg-rose-500',
};

export function tagDotClass(color: string): string {
  return DOT_CLASSES[color] ?? DOT_CLASSES.slate!;
}
